export const CONSENT_STORAGE_KEY = 'printen-cookie-consent-v2';
export const CONSENT_VERSION = 2 as const;

export type ConsentPreferences = {
  analytics: boolean;
  marketing: boolean;
  preferences: boolean;
};

export type StoredConsent = ConsentPreferences & {
  version: typeof CONSENT_VERSION;
  updatedAt: string;
};

export const DENIED_CONSENT: ConsentPreferences = Object.freeze({
  analytics: false,
  marketing: false,
  preferences: false,
});

type ReadableStorage = Pick<Storage, 'getItem'>;
type WritableStorage = Pick<Storage, 'setItem' | 'removeItem'>;

export function parseStoredConsent(value: string | null): StoredConsent | null {
  if (!value) return null;

  try {
    const parsed = JSON.parse(value) as Partial<StoredConsent>;
    if (
      parsed.version !== CONSENT_VERSION
      || typeof parsed.analytics !== 'boolean'
      || typeof parsed.marketing !== 'boolean'
      || typeof parsed.preferences !== 'boolean'
      || typeof parsed.updatedAt !== 'string'
      || Number.isNaN(Date.parse(parsed.updatedAt))
    ) {
      return null;
    }

    return parsed as StoredConsent;
  } catch {
    return null;
  }
}

export function readStoredConsent(storage?: ReadableStorage | null): StoredConsent | null {
  if (!storage) return null;
  try {
    return parseStoredConsent(storage.getItem(CONSENT_STORAGE_KEY));
  } catch {
    return null;
  }
}

export function getConsentSnapshot(storage?: ReadableStorage | null): ConsentPreferences {
  const stored = readStoredConsent(storage);
  return stored
    ? {
        analytics: stored.analytics,
        marketing: stored.marketing,
        preferences: stored.preferences,
      }
    : { ...DENIED_CONSENT };
}

export function storeConsent(
  storage: WritableStorage,
  preferences: ConsentPreferences,
  now = new Date(),
): StoredConsent {
  const stored: StoredConsent = {
    version: CONSENT_VERSION,
    analytics: preferences.analytics,
    marketing: preferences.marketing,
    preferences: preferences.preferences,
    updatedAt: now.toISOString(),
  };

  storage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(stored));

  // Keep the old keys in sync while remaining legacy components are phased out.
  storage.setItem('cookies-accepted', 'true');
  storage.setItem('analytics-enabled', String(stored.analytics));
  storage.setItem('marketing-enabled', String(stored.marketing));
  storage.setItem('functional-enabled', String(stored.preferences));

  return stored;
}

export function hasAnalyticsConsent(value: unknown): boolean {
  return Boolean(
    value
    && typeof value === 'object'
    && (value as Record<string, unknown>).analytics === true,
  );
}

export function hasMarketingConsent(value: unknown): boolean {
  return Boolean(
    value
    && typeof value === 'object'
    && (value as Record<string, unknown>).marketing === true,
  );
}

